import Logger from "~/services/logger/logger";

import invariant from "./invariant";

type WoosmapLocalityField =
  | "name"
  | "administrative_area_level_0"
  | "administrative_area_level_1"
  | "administrative_area_level_2"
  | "postal_codes"
  | "postal_code"
  | "postal_town"
  | "locality"
  | "iata_code"
  | "route"
  | "street_number"
  | "premise"
  | "organisation"
  | "post_box"
  | "district"
  | "county"
  | "country"
  | "state";

export type WooosmapGeometry = {
  location: {
    lat: number;
    lng: number;
  };
  viewport?: {
    northeast: {
      lat: number;
      lng: number;
    };
    southwest: {
      lat: number;
      lng: number;
    };
  };
};

export type WoosmapSuggestion = {
  address_components: { long_name: string; short_name: string; types: WoosmapLocalityField[] }[];
  name?: string;
  formatted_address: string;
  public_id: string;
  types?: WoosmapLocalityField[];
  geometry: WooosmapGeometry;
};

type WoosmapLocalitiesMappingCountry = Partial<
  Record<
    keyof Omit<EVA.Core.AddressDto, "ID">,
    { field: WoosmapLocalityField; component: "long_name" | "short_name" }[]
  >
>;

const defaultLocalitiesMapping = {
  Address1: [{ field: "route", component: "long_name" }],
  HouseNumber: [{ field: "street_number", component: "long_name" }],
  City: [{ field: "locality", component: "long_name" }],
  District: [{ field: "county", component: "short_name" }],
  State: [{ field: "state", component: "short_name" }],
  ZipCode: [
    { field: "postal_code", component: "short_name" },
    { field: "postal_codes", component: "short_name" },
  ],
} satisfies WoosmapLocalitiesMappingCountry;

const middleEastBaseLocalitiesMapping = {
  Address1: [{ field: "route", component: "long_name" }],
  City: [{ field: "locality", component: "long_name" }],
  District: [{ field: "district", component: "long_name" }],
  HouseNumber: [{ field: "street_number", component: "long_name" }],
  State: [{ field: "locality", component: "long_name" }],
  ZipCode: [{ field: "postal_codes", component: "long_name" }],
} satisfies WoosmapLocalitiesMappingCountry;

const WoosmapLocalitiesMapping = {
  AE: {
    ...middleEastBaseLocalitiesMapping,
  },
  QA: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  KW: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  BH: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  OM: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  JO: {
    ...middleEastBaseLocalitiesMapping,
    District: [{ field: "locality", component: "long_name" }],
  },
  US: {
    Address1: [
      { field: "street_number", component: "short_name" },
      { field: "route", component: "long_name" },
    ],
    City: [{ field: "locality", component: "long_name" }],
    District: [{ field: "locality", component: "long_name" }],
    State: [{ field: "state", component: "short_name" }],
    ZipCode: [{ field: "postal_codes", component: "short_name" }],
  },
  FR: {
    ...defaultLocalitiesMapping,
    City: [
      { field: "locality", component: "long_name" },
      { field: "postal_town", component: "long_name" },
    ],
    District: [
      { field: "county", component: "long_name" },
      { field: "administrative_area_level_1", component: "long_name" },
    ],
  },
  ES: {
    ...defaultLocalitiesMapping,
    State: [{ field: "state", component: "short_name" }],
  },
} satisfies Record<string, WoosmapLocalitiesMappingCountry>;

const woosmapUtils = {
  mapSuggestionToAddressDto(suggestion: WoosmapSuggestion, countryID: string): Omit<EVA.Core.AddressDto, "ID"> {
    let address: Omit<EVA.Core.AddressDto, "ID"> = {};
    const countryMapping =
      WoosmapLocalitiesMapping[countryID as keyof typeof WoosmapLocalitiesMapping] || defaultLocalitiesMapping;

    for (const [dtoField, rules] of Object.entries(countryMapping)) {
      const values = (rules as { field: WoosmapLocalityField; component: "long_name" | "short_name" }[])
        .map((rule) => suggestion.address_components.find((ac) => ac.types.includes(rule.field))?.[rule.component])
        .filter(Boolean);

      if (values.length > 0) {
        // @ts-expect-error - Dynamic access to address field
        address[dtoField] = values.join(" ");
      }
    }

    return address;
  },

  async getAddressDetails(publicId: string, language?: string) {
    try {
      const params = new URLSearchParams({
        key: process.env.NEXT_PUBLIC_WOOSMAP_API_KEY!,
        public_id: publicId,
      });

      if (language) {
        params.set("language", language);
      }

      const res = await fetch(`https://api.woosmap.com/address/details/json?${params}`);

      if (!res.ok) {
        throw await res.text();
      }

      return await res.json();
    } catch {
      Logger.instance.warn(`autocomplete: couldn't get address detail for id ${publicId}`);
      return null;
    }
  },

  async getAddressState(address: string, countryID: string, language?: string) {
    try {
      const params = new URLSearchParams({
        key: process.env.NEXT_PUBLIC_WOOSMAP_API_KEY!,
        components: `country:${countryID}`,
        fields: "address_components",
        address,
      });

      if (language) {
        params.set("language", language);
      }

      const res = await fetch(`https://api.woosmap.com/localities/geocode?${params}`);

      if (!res.ok) {
        throw await res.text();
      }

      const json = await res.json();

      invariant(Array.isArray(json.results), `Got result from Woosmap but results is not an array.`);

      if (json.results.length === 0) {
        Logger.instance.warn("Could not find state for address: empty results");
        return null;
      }

      const components = json.results[0].address_components;

      invariant(Array.isArray(components), `Got result from Woosmap but address_components is not an array.`);

      if (components.length === 0) {
        Logger.instance.warn("Could not find state for address: empty address_components");
        return null;
      }

      const state = components.find((c) => c.types.includes("state"));

      if (!state) {
        return null;
      }

      return state.short_name as string;
    } catch (err) {
      Logger.instance.error("Could not find state for address:", err);
      return null;
    }
  },
};

export default woosmapUtils;
